"use client";

import React from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import type { ContactMethodEntityContext } from "@/lib/utils/crm/contact-method-context";
import type { BaseEntity, SimpleTableConfig } from "../../UniversalCRMTable/types";
import { useFieldEditing } from "../../InlineEditableInfoPanel/hooks/useFieldEditing";
import type { DetailHeaderModel, HeaderFact } from "./model";
import { MoreToggle } from "./DetailHeaderModelView/MoreToggle";
import { FactValue, isEmptyFact } from "./DetailHeaderModelView/FactValue";

// Lazy + client-only: the in-header editor transitively pulls the heavy field
// input renderer (selects, calendar, etc.). Keeping it OUT of this module's
// static graph avoids a circular import through the entity registry and means
// the editor's bundle only loads when a user actually edits a header field.
//
// NOTE: this specifier is resolved relative to THIS file. `HeaderEditableField`
// is a sibling of this module, not of the `DetailHeaderModelView/` directory —
// do not move this import into that directory without repointing it.
const HeaderEditableField = dynamic(
  () => import("./HeaderEditableField").then((m) => m.HeaderEditableField),
  { ssr: false }
) as unknown as typeof import("./HeaderEditableField").HeaderEditableField;

// Higher caps on desktop: the facts row + metrics strip wrap horizontally, so
// there is plenty of width before a "+N more" toggle is needed. Keeping these
// generous fills the horizontal space and avoids burying numbers behind a
// toggle the user has to expand.
const FACTS_VISIBLE = 6;
const METRICS_VISIBLE = 6;

/**
 * Renders the typed `DetailHeaderModel`: subtitle segments, a facts row, and
 * a metrics strip. Entity knowledge lives in the per-entity builders under
 * `header/entities/`. Facts/metrics whose builder attached a `fieldId` (1:1
 * scalar columns) are edited in place via `HeaderEditableField`, reusing the
 * inline panel's `useFieldEditing` save path; those same fields are dropped
 * from the inline edit panel so each has exactly one editor.
 *
 * The per-fact value renderers live in `DetailHeaderModelView/FactValue.tsx`
 * and the capped-region toggle in `DetailHeaderModelView/MoreToggle.tsx`
 * (module-size lane L17).
 */
export function DetailHeaderModelView<T extends BaseEntity>({
  model,
  fallbackDescription,
  item,
  config,
  onInlineEdit,
  canEdit = false,
}: {
  model: DetailHeaderModel;
  fallbackDescription: string;
  item: T;
  config: SimpleTableConfig<T>;
  onInlineEdit?: (item: T) => Promise<void>;
  canEdit?: boolean;
}) {
  const [factsExpanded, setFactsExpanded] = React.useState(false);
  const [metricsExpanded, setMetricsExpanded] = React.useState(false);

  const editingEnabled = canEdit && typeof onInlineEdit === "function";
  const { editingField, editedItem, handleFieldEdit, handleSaveField, handleCancelEdit, handleFieldValueChange } =
    useFieldEditing<T>(item, onInlineEdit);

  /**
   * Wrap a header element's display node in the in-place editor when it carries
   * an editable fieldId. `forceEditAffordance` keeps the edit control always
   * visible (used for empty fields showing an "Add <field>" prompt, so the
   * inline-add is obvious without hovering).
   */
  const renderEditable = (
    fieldId: string | undefined,
    display: React.ReactNode,
    forceEditAffordance = false
  ): React.ReactNode => {
    if (!fieldId || !editingEnabled || !config?.fields?.[fieldId]) return display;
    return (
      <HeaderEditableField<T>
        fieldId={fieldId}
        displayContent={display}
        item={item}
        config={config}
        isEditing={editingField === fieldId}
        editedValue={editedItem[fieldId as keyof T]}
        forceEditAffordance={forceEditAffordance}
        onEdit={() => handleFieldEdit(fieldId)}
        onSave={() => handleSaveField(fieldId)}
        onCancel={handleCancelEdit}
        onValueChange={(newValue) => handleFieldValueChange(fieldId, newValue)}
      />
    );
  };

  /** Drop an empty fact that the viewer cannot fill (no editable field, or read-only). */
  const canShowFact = (fact: HeaderFact): boolean =>
    !isEmptyFact(fact) || (Boolean(fact.fieldId) && editingEnabled && Boolean(config?.fields?.[fact.fieldId as string]));

  const entityContext = React.useMemo<ContactMethodEntityContext | undefined>(() => {
    if (!item || !config?.entity) return undefined;
    return {
      item: item as Record<string, unknown>,
      entityType: config.entity,
    };
  }, [item, config?.entity]);

  // Drop empty facts the viewer can't fill (read-only / non-editable) BEFORE
  // capping, so a dead placeholder never consumes a visible slot or a "+N more".
  const shownFacts = model.facts.filter(canShowFact);
  const factsHidden = Math.max(0, shownFacts.length - FACTS_VISIBLE);
  const metricsHidden = Math.max(0, model.metrics.length - METRICS_VISIBLE);
  const visibleFacts = factsExpanded ? shownFacts : shownFacts.slice(0, FACTS_VISIBLE);
  const visibleMetrics = metricsExpanded ? model.metrics : model.metrics.slice(0, METRICS_VISIBLE);

  return (
    <div className="space-y-1 md:space-y-2">
      {model.subtitleSegments.length > 0 ? (
        <div className="text-gray-600 dark:text-gray-400 text-sm md:text-lg flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {model.subtitleSegments.map((segment, index) => (
            <React.Fragment key={`${segment.text}-${index}`}>
              {index > 0 && <span aria-hidden="true">•</span>}
              {segment.href ? (
                <Link
                  href={segment.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {segment.text}
                </Link>
              ) : (
                <span>{segment.text}</span>
              )}
            </React.Fragment>
          ))}
        </div>
      ) : (
        fallbackDescription && (
          <div className="text-gray-600 dark:text-gray-400 text-sm md:text-lg">
            {fallbackDescription}
          </div>
        )
      )}

      {/* Gate on what actually RENDERS, not on the unfiltered model: a header
          whose facts are all dropped (empty and not fillable by this viewer)
          used to still emit this flex row, an empty element contributing the
          parent's `space-y-1` gap. */}
      {shownFacts.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 md:gap-4 text-xs md:text-sm text-gray-500">
          {visibleFacts.map((fact) => {
            const Icon = fact.icon;
            return (
              <div key={fact.id} className="flex items-center gap-1 min-w-0">
                <Icon className="h-4 w-4 flex-shrink-0" />
                {fact.label && <span className="text-gray-400">{fact.label}:</span>}
                {renderEditable(
                  fact.fieldId,
                  <FactValue fact={fact} entityContext={entityContext} />,
                  isEmptyFact(fact)
                )}
                {fact.badge && !isEmptyFact(fact) && (
                  <span
                    title={fact.badge.title}
                    className="flex-shrink-0 rounded-full border border-gray-300 dark:border-gray-600 px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
                  >
                    {fact.badge.text}
                  </span>
                )}
              </div>
            );
          })}
          {factsHidden > 0 && (
            <MoreToggle
              hiddenCount={factsHidden}
              expanded={factsExpanded}
              onToggle={() => setFactsExpanded((v) => !v)}
            />
          )}
        </div>
      )}

      {model.metrics.length > 0 && (
        <div className="flex flex-wrap items-baseline gap-x-4 md:gap-x-5 gap-y-1 pt-0.5">
          {visibleMetrics.map((metric) => (
            // Inline "LABEL value" pair on every breakpoint so metrics wrap
            // horizontally across the header width instead of each becoming a
            // 2-line stacked block (the old `md:block`) that wasted vertical space.
            <div key={metric.id} className="flex items-baseline gap-1.5 min-w-0">
              <span className="text-[10px] md:text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 flex-shrink-0">
                {metric.label}
              </span>
              {renderEditable(
                metric.fieldId,
                <span className="text-xs md:text-sm font-semibold text-card-foreground truncate">
                  {metric.value}
                  {metric.hint && (
                    <span className="ml-0.5 text-[10px] md:text-xs font-normal text-gray-500">{metric.hint}</span>
                  )}
                </span>
              )}
            </div>
          ))}
          {metricsHidden > 0 && (
            <MoreToggle
              hiddenCount={metricsHidden}
              expanded={metricsExpanded}
              onToggle={() => setMetricsExpanded((v) => !v)}
            />
          )}
        </div>
      )}
    </div>
  );
}
